import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Modal, Radio, Skeleton, Tag, Typography } from 'antd'
import { CheckOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'
import { format } from 'date-fns'

import { getDateFnsLocale } from '@vacationtracker/shared/i18n'

import IntlMessages from '../../../../util/IntlMessages'
import { useCurrency } from '../../../../hooks/use-currency'

import { IImportTableRow } from '../types'
import { IUpcomingInvoiceInformation } from '@vacationtracker/shared/types/stripe'
import { IGetLocationsShort } from '../../../../types/locations'
import { IGetLeaveTypeList } from '../../../../types/custom-queries'
import { LocaleEnum } from '@vacationtracker/shared/types/i18n'

const { Paragraph, Text } = Typography

interface ILeaveTypeOption {
  id: string
  name: string
}

interface IConfirmModalProps {
  open: boolean
  tableRows: IImportTableRow[]
  importAllUsers?: boolean
  locations: IGetLocationsShort[]
  leaveTypes: IGetLeaveTypeList[]
  defaultLocationId: string
  companyShouldProrate: boolean
  companyProratedLeaveTypes: string[]
  upcomingInvoiceInfo: IUpcomingInvoiceInformation | null
  isLoadingUpcomingInvoiceInfo: boolean
  isAnnualBilling: boolean
  isTrial: boolean
  isFreemium: boolean
  locale: LocaleEnum
  onConfirm: (shouldProrate: boolean, prorateLeaveTypes: string[]) => void
  onCancel: () => void
}

const ConfirmModal = ({
  open,
  tableRows,
  importAllUsers,
  locations,
  leaveTypes,
  defaultLocationId,
  companyShouldProrate,
  companyProratedLeaveTypes,
  upcomingInvoiceInfo,
  isLoadingUpcomingInvoiceInfo,
  isAnnualBilling,
  isTrial,
  isFreemium,
  locale,
  onConfirm,
  onCancel,
}: IConfirmModalProps): React.ReactElement => {
  const { formatMessage } = useIntl()
  const { formatPrice } = useCurrency()
  const [quotaMode, setQuotaMode] = useState<'full' | 'partial'>(companyShouldProrate ? 'partial' : 'full')
  const [selectedLeaveTypeIds, setSelectedLeaveTypeIds] = useState<string[]>([])

  // Determine which locations are used by the users being imported
  const usedLocationIds = useMemo(() => {
    const ids = new Set<string>()
    for (const row of tableRows) {
      ids.add(row.locationId)
    }
    return Array.from(ids)
  }, [tableRows])

  // Check if any used location has fiscal year reset (proration only makes sense then)
  const hasFiscalYearLocation = useMemo(() => {
    return usedLocationIds.some((locId) => {
      const loc = locations.find((l) => l.id === locId)
      return loc?.resetQuotas === 'FISCAL_YEAR'
    })
  }, [usedLocationIds, locations])

  // Get leave types that have policies for the used locations
  // Exclude leave types where all matching policies have unlimited days or 0 days/hours
  const availableLeaveTypes: ILeaveTypeOption[] = useMemo(() => {
    return leaveTypes
      .filter((lt) => lt.isActive)
      .filter((lt) =>
        lt.leavePolicies.some((lp) => {
          if (lp.locationId == null || !usedLocationIds.includes(lp.locationId)) return false
          if (lp.hasUnlimitedDays) return false
          if ((lp.daysPerYear ?? 0) === 0 && (lp.hoursPerYear ?? 0) === 0) return false
          return true
        })
      )
      .map((lt) => ({ id: lt.id, name: lt.name }))
  }, [leaveTypes, usedLocationIds])

  // Initialize selected leave types from company defaults or all available
  useEffect(() => {
    if (!open) return

    if (companyProratedLeaveTypes.length > 0) {
      const filtered = companyProratedLeaveTypes.filter((id) => availableLeaveTypes.some((lt) => lt.id === id))
      setSelectedLeaveTypeIds(filtered.length > 0 ? filtered : availableLeaveTypes.map((lt) => lt.id))
    } else {
      setSelectedLeaveTypeIds(availableLeaveTypes.map((lt) => lt.id))
    }
    setQuotaMode(companyShouldProrate ? 'partial' : 'full')
  }, [open, availableLeaveTypes, companyShouldProrate, companyProratedLeaveTypes])

  const toggleLeaveType = useCallback((id: string) => {
    setSelectedLeaveTypeIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }, [])

  const handleConfirm = useCallback(() => {
    const shouldProrate = quotaMode === 'partial' && hasFiscalYearLocation
    onConfirm(shouldProrate, shouldProrate ? selectedLeaveTypeIds : [])
  }, [onConfirm, quotaMode, hasFiscalYearLocation, selectedLeaveTypeIds])

  const showBilling = !isTrial && !isFreemium

  return (
    <Modal
      title={formatMessage({ id: 'components.importUsersForm.confirmModalTitle' })}
      open={open}
      onOk={handleConfirm}
      onCancel={onCancel}
      okText={formatMessage({ id: 'components.importUsersForm.confirmModalImportNow' })}
      cancelText={formatMessage({ id: 'app.back' })}
      okButtonProps={{ disabled: tableRows.length === 0 && !importAllUsers }}
      width={560}
    >
      {/* Billing summary */}
      {showBilling && (
        <div style={{ marginBottom: 20 }}>
          {isLoadingUpcomingInvoiceInfo ? (
            <Skeleton.Button size="large" shape="square" style={{ width: '100%' }} active />
          ) : upcomingInvoiceInfo ? (
            <>
              {upcomingInvoiceInfo.isTrial ? (
                <Alert
                  message={formatMessage(
                    { id: 'components.importUsersForm.warningOfUsersImportTrial' },
                    {
                      amount: formatPrice(Number(upcomingInvoiceInfo.nextInvoiceAmount)),
                      startDate: format(new Date(upcomingInvoiceInfo.nextInvoiceDate as string), 'PPP', { locale: getDateFnsLocale(locale) }),
                    }
                  )}
                  type="info"
                  showIcon
                />
              ) : upcomingInvoiceInfo.isSameInvoiceAmount ? (
                <Alert message={formatMessage({ id: 'components.importUsersForm.warningOfUsersImportNoChange' }, { noOfUsers: tableRows.length })} type="success" showIcon />
              ) : (
                <div style={{ background: '#f6f8fa', borderRadius: 8, padding: '12px 16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <Text type="secondary">{formatMessage({ id: 'components.importUsersForm.confirmModalBillingUsersAdded' })}</Text>
                    <Text strong>{tableRows.length}</Text>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <Text type="secondary">{formatMessage({ id: 'components.importUsersForm.confirmModalBillingNewTotal' })}</Text>
                    <Text strong>
                      {formatPrice(Number(upcomingInvoiceInfo.nextInvoiceAmount))}/{isAnnualBilling ? 'yr' : 'mo'}
                    </Text>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: isAnnualBilling && upcomingInvoiceInfo.prorateAmount > 0 ? 6 : 0 }}>
                    <Text type="secondary">{formatMessage({ id: 'components.importUsersForm.confirmModalBillingBilledFrom' })}</Text>
                    <Text strong>{format(new Date(upcomingInvoiceInfo.nextInvoiceDate as string), 'PPP', { locale: getDateFnsLocale(locale) })}</Text>
                  </div>
                  {isAnnualBilling && upcomingInvoiceInfo.prorateAmount > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text type="secondary">{formatMessage({ id: 'components.importUsersForm.confirmModalBillingProrateCharge' })}</Text>
                      <Text strong>{formatPrice(Number(upcomingInvoiceInfo.prorateAmount))}</Text>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : null}
        </div>
      )}

      {/* Quota section - only show if location uses fiscal year */}
      {hasFiscalYearLocation && availableLeaveTypes.length > 0 && (
        <>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: '#6B7280',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: 10,
            }}
          >
            <IntlMessages id="components.importUsersForm.confirmModalQuotaLabel" />
          </div>

          <div style={{ marginBottom: 14 }}>
            <Text style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>
              <IntlMessages id="components.importUsersForm.confirmModalQuotaQuestion" />
            </Text>
            <Radio.Group optionType="button" value={quotaMode} onChange={(e) => setQuotaMode(e.target.value)}>
              <Radio.Button value="full">{formatMessage({ id: 'components.importUsersForm.confirmModalFullYear' })}</Radio.Button>
              <Radio.Button value="partial">{formatMessage({ id: 'components.importUsersForm.confirmModalPartialYear' })}</Radio.Button>
            </Radio.Group>
          </div>

          {quotaMode === 'partial' && (
            <div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                {availableLeaveTypes.map((lt) => {
                  const isSelected = selectedLeaveTypeIds.includes(lt.id)
                  return (
                    <Tag
                      key={lt.id}
                      onClick={() => toggleLeaveType(lt.id)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        padding: '4px 10px 4px 8px',
                        border: `1.5px solid ${isSelected ? '#7C3AED' : '#D1D5DB'}`,
                        borderRadius: 99,
                        background: isSelected ? '#EDE9FE' : 'white',
                        color: isSelected ? '#7C3AED' : '#6B7280',
                        fontWeight: isSelected ? 600 : 400,
                        fontSize: 12,
                        cursor: 'pointer',
                        margin: 0,
                      }}
                    >
                      {isSelected && <CheckOutlined style={{ fontSize: 10 }} />}
                      {lt.name}
                    </Tag>
                  )
                })}
              </div>
              <Paragraph style={{ fontSize: 12, color: '#9CA3AF', lineHeight: 1.5 }}>
                <IntlMessages id="components.importUsersForm.confirmModalProrateNote" />
              </Paragraph>
            </div>
          )}
        </>
      )}

      <Paragraph style={{ marginTop: 16, color: '#888', fontSize: 13 }}>
        <IntlMessages id="components.importUsersForm.confirmImportUsersModalContentChangeLaterInfo" />
      </Paragraph>
    </Modal>
  )
}

export default ConfirmModal
